import { inject, Injectable } from "@angular/core";

import {
    HtmlContext,
    HtmlSequence,
    HtmlDirectiveParser,
    HtmlDirectiveEvaluator,
    HtmlDirectiveParserTable,
    HtmlExpressionParserConfig,
    HtmlDirectiveEvaluatorTable,
    HtmlExpressionResolverSettings,
} from "@main/reporting.v3/widget.html/models";
import {
    HtmlNumberDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-number-directive.service";
import {
    HtmlStringDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-string-directive.service";
import {
    HtmlWidgetDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-widget-directive.service";
import {
    HtmlForLoopDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-for-loop-directive.service";
import {
    HtmlForLoopDirectiveParserService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-expression-parsers/html-for-loop-directive-parser.service";
import {
    HtmlBaseExpressionsResultService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-base-expressions-result.service";
import {
    HtmlBaseExpressionsParserService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-base-expressions-parser.service";
import {
    SequentialTaskHandlerService
} from "@main/reporting.v3/core/concurrency/services/sequential-task-handler.service";
import {
    HtmlBaseExpressionsEvaluatorService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-base-expressions-evaluator.service";
import {
    HtmlBoolDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-bool-directive.service";
import {
    HtmlEvalDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-eval-directive.service";
import {
    HtmlRangeDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-range-directive.service";
import {
    HtmlQueryDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-query-directive.service";
import {
    HtmlIndexDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-index-directive.service";


@Injectable()
export class WidgetHtmlExpressionsResolverService {

    private forLoopParserService: HtmlForLoopDirectiveParserService = inject(HtmlForLoopDirectiveParserService);
    private evaluatorService: HtmlBaseExpressionsEvaluatorService = inject(HtmlBaseExpressionsEvaluatorService);
    private sequentialTaskHandlerService: SequentialTaskHandlerService = inject(SequentialTaskHandlerService);
    private resultService: HtmlBaseExpressionsResultService = inject(HtmlBaseExpressionsResultService);
    private parserService: HtmlBaseExpressionsParserService = inject(HtmlBaseExpressionsParserService);
    private forLoopDirectiveService: HtmlForLoopDirectiveService = inject(HtmlForLoopDirectiveService);
    private widgetDirectivesService: HtmlWidgetDirectiveService = inject(HtmlWidgetDirectiveService)
    private stringDirectiveService: HtmlStringDirectiveService = inject(HtmlStringDirectiveService);
    private numberDirectiveService: HtmlNumberDirectiveService = inject(HtmlNumberDirectiveService);
    private queryDirectiveService: HtmlQueryDirectiveService = inject(HtmlQueryDirectiveService);
    private rangeDirectiveService: HtmlRangeDirectiveService = inject(HtmlRangeDirectiveService);
    private indexDirectiveService: HtmlIndexDirectiveService = inject(HtmlIndexDirectiveService);
    private evalDirectiveService: HtmlEvalDirectiveService = inject(HtmlEvalDirectiveService);
    private boolDirectiveService: HtmlBoolDirectiveService = inject(HtmlBoolDirectiveService);

    public async resolveText(context: HtmlContext, content: string, config: HtmlExpressionParserConfig = {}): Promise<string> {
        if (!context || !content) return null;

        // Record of <directive name, parser>.
        const parsers: HtmlDirectiveParserTable = this.getDirectiveParserTable();

        // Record of <directive name, evaluator>.
        const evaluators: HtmlDirectiveEvaluatorTable = this.getDirectiveEvaluatorTable();

        // List of directive names to parse (only the ones that have defined evaluators).
        const directives: string[] = Object.keys(evaluators ?? {});

        const settings: HtmlExpressionResolverSettings = { ...config, context, parsers, evaluators, directives };

        const sequence: HtmlSequence = this.parserService.parse(content, settings);
        const results: any[] = await this.evaluatorService.evaluateSequence(sequence, settings);

        return this.resultService.collapse(results.filter(result => result !== null)) ?? "";
    }

    public async resolveTree(context: HtmlContext, node: HTMLElement, config: HtmlExpressionParserConfig = {}): Promise<void> {
        if (!context || !node) return;

        // Record of <directive name, parser>.
        const parsers: HtmlDirectiveParserTable = this.getDirectiveParserTable();

        // Record of <directive name, evaluator>.
        const evaluators: HtmlDirectiveEvaluatorTable = this.getDirectiveEvaluatorTable();

        // List of directive names to parse (only the ones that have defined evaluators).
        const directives: string[] = Object.keys(evaluators ?? {});

        const settings: HtmlExpressionResolverSettings = { ...config, context, parsers, evaluators, directives };

        return await this.resolveNode(node, settings);
    }

    private async resolveNode(node: HTMLElement, settings: HtmlExpressionResolverSettings): Promise<void> {
        if (!node || !settings) return;

        const children: ChildNode[] = Array.from(node.childNodes);

        // Recursively resolve directives in children nodes.
        const promises: Promise<void>[] = children.map(async child => {

            if (!(child instanceof HTMLElement)) return;

            await this.resolveNode(child, settings);
        });

        // Resolve the directives in this node content and attributes.
        promises.push(
            this.resolveAttributes(node, settings),
            this.resolveChildren(node, settings),
        );

        await Promise.all(promises);
    }

    private async resolveAttributes(node: HTMLElement, settings: HtmlExpressionResolverSettings): Promise<void> {
        if (!node || !settings) return;

        const attributes: string[] = node?.getAttributeNames();

        // Get the promises corresponding to the resolution of each attribute.
        const promises: Promise<void>[] = attributes.map(async attribute => {

            if (!attribute) return;

            await this.resolveAttribute(node, attribute, settings);
        });

        // Resolve the directives of each attribute in parallel.
        await Promise.all(promises);
    }

    private async resolveAttribute(node: HTMLElement, attribute: string, settings: HtmlExpressionResolverSettings): Promise<void> {
        if (!node || !settings || !attribute) return;

        const value: string = node.getAttribute(attribute);
        const sequence: HtmlSequence = this.parserService.parse(value, settings);

        if (!value?.length || !sequence?.children) return;

        // Temporarily set the attribute value without directive tags, to not consider them until they resolve.
        const normalized: string = this.evaluatorService.normalizeSequence(sequence);

        node.setAttribute(attribute, normalized);

        // Resolve directives present in the attribute value.
        const results: any[] = await this.evaluatorService.evaluateSequence(sequence, settings);

        const result: string = this.resultService.collapse(results.filter(result => result !== null));

        // Finally set the attribute value with directive tags resolved.
        node.setAttribute(attribute, result);
    }

    private async resolveChildren(node: HTMLElement, settings: HtmlExpressionResolverSettings): Promise<void> {
        if (!node || !settings) return;

        // Recursively resolve directives in children text nodes.
        const children: ChildNode[] = Array.from(node.childNodes);

        const promises: Promise<void>[] = children.map(async child => {

            if (!(child instanceof Text)) return;

            await this.resolveTextNode(child, settings);
        });

        // Resolve all children promises in parallel.
        await Promise.all(promises);
    }

    private async resolveTextNode(node: Text, settings: HtmlExpressionResolverSettings): Promise<void> {
        if (!node || !settings) return;

        // Resolve expressions present in text node contents.
        const sequence: HtmlSequence = this.parserService.parse(node.textContent, settings);

        // Temporarily set the content without directive tags, to not show them until they resolve.
        node.textContent = this.evaluatorService.normalizeSequence(sequence);

        // Resolve directives present in the content.
        const results: any[] = await this.evaluatorService.evaluateSequence(sequence, settings);

        // Convert all result to DOM supported values.
        const children: (string | Node)[] = results.map(result => this.resultService.convert(result));

        const parent: HTMLElement = node.parentElement;
        const next: ChildNode = node.nextSibling;

        if (!parent) return;

        return this.sequentialTaskHandlerService.handleTask(() => {
            // Remove this child.
            parent.removeChild(node);

            children.filter(child => !!child).forEach(child => {
                // Insert each non-null resolved children in the parent node.
                parent.insertBefore(child instanceof Node ? child : document.createTextNode(child), next);
            });
        });
    }

    private getDirectiveEvaluatorTable(): HtmlDirectiveEvaluatorTable {
        // Record of <directive name, evaluator>.
        const table: HtmlDirectiveEvaluatorTable = {};

        // Register the HTML evaluators to resolve HTML expressions.
        const evaluators: HtmlDirectiveEvaluator[] = [
            this.forLoopDirectiveService,
            this.widgetDirectivesService,
            this.numberDirectiveService,
            this.stringDirectiveService,
            this.queryDirectiveService,
            this.rangeDirectiveService,
            this.indexDirectiveService,
            this.evalDirectiveService,
            this.boolDirectiveService,
        ];

        // Build the evaluator table.
        evaluators.forEach(evaluator => table[evaluator.directive] = evaluator);

        return table;
    }

    private getDirectiveParserTable(): HtmlDirectiveParserTable {
        // Record of <directive name, parser>.
        const table: HtmlDirectiveParserTable = {};

        // Register the HTML parsers to resolve HTML expressions.
        const parsers: HtmlDirectiveParser[] = [
            this.forLoopParserService,
        ];

        // Build the parser table.
        parsers.forEach(parser => table[parser.directive] = parser);

        return table;
    }
}